import { useRef, useEffect, useCallback } from 'react';
import { ValuationData } from '../types/valuation';
import {
  captureFinancialInputs,
  logTamSuggestionEvent,
  markSessionPaymentComplete,
  CaptureTrigger,
  FieldSource,
  FieldSourceMap,
} from '../services/dataFlywheelService';
import { TamEstimationResult } from './useTamEstimation';

interface UseDataFlywheelOptions {
  data: ValuationData;
  isAuthenticated: boolean;
  userId: string | undefined;
  sessionId: string;
  tamResult: TamEstimationResult | null;
  currentStep: number;
  propertyCounty?: string;
}

export function useDataFlywheel({
  data,
  isAuthenticated,
  userId,
  sessionId,
  tamResult,
  currentStep,
  propertyCounty,
}: UseDataFlywheelOptions) {
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastCapturedJsonRef = useRef<string>('');

  // Track the source of each field value
  const fieldSourcesRef = useRef<FieldSourceMap>({});

  // Track which TAM fields were accepted (value at acceptance time)
  const acceptedTamFieldsRef = useRef<Record<string, number>>({});

  // ---------------------------------------------------------------
  // Field source helpers
  // ---------------------------------------------------------------

  const getFieldSources = useCallback((): FieldSourceMap => {
    return { ...fieldSourcesRef.current };
  }, []);

  const setFieldSource = useCallback((fieldName: string, source: FieldSource) => {
    fieldSourcesRef.current[fieldName] = source;
  }, []);

  // ---------------------------------------------------------------
  // Core capture function
  // ---------------------------------------------------------------

  const capture = useCallback(
    (trigger: CaptureTrigger) => {
      if (!isAuthenticated || !userId) return;
      if (currentStep !== 4) return;

      // De-duplicate: skip if snapshot hasn't changed (debounce only)
      const snapshot = JSON.stringify({
        r: [data.annualGallons, data.fuelMargin, data.storeRevenue, data.storeMargin,
            data.carWashRevenue, data.carWashMargin, data.qsrRepairBayRevenue,
            data.qsrRepairBayMargin, data.totalGrossProfit],
        e: [data.operatingExpenses, data.utilities, data.salaries, data.insurance,
            data.repairsAndMaintenance, data.propertyTax, data.advertisingAndPromotion,
            data.merchantFees, data.generalAndAdmin],
      });

      if (trigger === 'debounce' && snapshot === lastCapturedJsonRef.current) return;
      lastCapturedJsonRef.current = snapshot;

      captureFinancialInputs({
        userId,
        sessionId,
        stationObjectId: data.stationObjectId,
        propertyAddress: data.location,
        propertyCounty,
        captureTrigger: trigger,
        data,
        fieldSources: getFieldSources(),
        tamResultAvailable: !!tamResult,
      });
    },
    [isAuthenticated, userId, sessionId, data, tamResult, currentStep, propertyCounty, getFieldSources]
  );

  // ---------------------------------------------------------------
  // Debounced capture (5s after last financial field change)
  // ---------------------------------------------------------------

  useEffect(() => {
    if (currentStep !== 4 || !isAuthenticated) return;

    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

    debounceTimerRef.current = setTimeout(() => {
      capture('debounce');
    }, 5000);

    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [
    data.annualGallons, data.fuelMargin, data.storeRevenue, data.storeMargin,
    data.carWashRevenue, data.carWashMargin, data.qsrRepairBayRevenue,
    data.qsrRepairBayMargin, data.totalGrossProfit,
    data.operatingExpenses, data.utilities, data.salaries, data.insurance,
    data.repairsAndMaintenance, data.propertyTax, data.advertisingAndPromotion,
    data.merchantFees, data.generalAndAdmin,
    data.hasLeasedQSR, data.qsrCurrentMonthlyRent,
    currentStep, isAuthenticated, capture,
  ]);

  // ---------------------------------------------------------------
  // Visibility change / page unload captures
  // ---------------------------------------------------------------

  useEffect(() => {
    if (!isAuthenticated || currentStep !== 4) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        capture('visibility_change');
      }
    };

    const handleBeforeUnload = () => {
      capture('page_unload');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isAuthenticated, currentStep, capture]);

  // ---------------------------------------------------------------
  // Step transition / view results capture (imperative)
  // ---------------------------------------------------------------

  const captureOnStepTransition = useCallback(
    (trigger: CaptureTrigger) => {
      capture(trigger);
    },
    [capture]
  );

  // ---------------------------------------------------------------
  // TAM suggestion: accept
  // ---------------------------------------------------------------

  const logTamAccept = useCallback(
    (fieldName: string, suggestedValue: number, currentUserValue: number) => {
      if (!isAuthenticated || !userId) return;

      // Mark field source as TAM-accepted
      setFieldSource(fieldName, 'tam_accepted');
      acceptedTamFieldsRef.current[fieldName] = suggestedValue;

      logTamSuggestionEvent({
        userId,
        sessionId,
        stationObjectId: data.stationObjectId,
        propertyAddress: data.location,
        propertyCounty,
        fieldName,
        suggestedValue,
        userValue: currentUserValue,
        action: 'accepted',
        tamCohortTier: tamResult?.diagnostics?.cohort?.tier,
        tamCohortN: tamResult?.diagnostics?.cohort?.n,
      });
    },
    [isAuthenticated, userId, sessionId, data.stationObjectId, data.location, propertyCounty, tamResult, setFieldSource]
  );

  // ---------------------------------------------------------------
  // TAM suggestion: dismiss (user typed manually instead)
  // ---------------------------------------------------------------

  const logTamDismiss = useCallback(
    (fieldName: string, suggestedValue: number, userValue: number) => {
      if (!isAuthenticated || !userId) return;

      logTamSuggestionEvent({
        userId,
        sessionId,
        stationObjectId: data.stationObjectId,
        propertyAddress: data.location,
        propertyCounty,
        fieldName,
        suggestedValue,
        userValue,
        action: 'dismissed',
        tamCohortTier: tamResult?.diagnostics?.cohort?.tier,
        tamCohortN: tamResult?.diagnostics?.cohort?.n,
      });
    },
    [isAuthenticated, userId, sessionId, data.stationObjectId, data.location, propertyCounty, tamResult]
  );

  // ---------------------------------------------------------------
  // TAM suggestion: modified after accept
  // ---------------------------------------------------------------

  const checkModifiedAfterAccept = useCallback(
    (fieldName: string, newValue: number) => {
      if (!isAuthenticated || !userId) return;

      const acceptedValue = acceptedTamFieldsRef.current[fieldName];
      if (acceptedValue !== undefined && acceptedValue !== newValue) {
        // Update field source
        setFieldSource(fieldName, 'tam_modified');

        logTamSuggestionEvent({
          userId,
          sessionId,
          stationObjectId: data.stationObjectId,
          propertyAddress: data.location,
          propertyCounty,
          fieldName,
          suggestedValue: acceptedValue,
          userValue: newValue,
          action: 'modified_after_accept',
          tamCohortTier: tamResult?.diagnostics?.cohort?.tier,
          tamCohortN: tamResult?.diagnostics?.cohort?.n,
        });

        // Clear so we don't log again for same field
        delete acceptedTamFieldsRef.current[fieldName];
      }
    },
    [isAuthenticated, userId, sessionId, data.stationObjectId, data.location, propertyCounty, tamResult, setFieldSource]
  );

  // ---------------------------------------------------------------
  // Payment completion
  // ---------------------------------------------------------------

  const markPaymentComplete = useCallback(() => {
    if (!userId) return;
    markSessionPaymentComplete(sessionId, userId);
  }, [sessionId, userId]);

  return {
    captureOnStepTransition,
    logTamAccept,
    logTamDismiss,
    checkModifiedAfterAccept,
    markPaymentComplete,
    getFieldSources,
  };
}
